/**
 * Shareable permalink: compress the raw.eml source into the URL hash so it
 * survives copy-paste without any backend. Uses `CompressionStream` (gzip),
 * which has been baseline in all evergreen browsers since 2023.
 *
 * The hash format is
 *  `#eml=1:<base64url>[&tab=<inspector-tab>][&filters=<base64url-json>]`
 * — the `1:` prefix lets us version the compression scheme if we ever
 * need to change it, the optional `&tab=` segment lets the link land
 * directly on the tab the sender was looking at, and the optional
 * `&filters=` segment carries a small JSON blob describing the active
 * Headers (category + query) and Links (filter + query) filter state at
 * encode time. The decoder accepts the older two-segment format too —
 * missing fields fall back to defaults.
 *
 * No bytes ever leave the browser; anyone you share the URL with runs the
 * decompression locally too.
 */

import { asBlobPart, bytesToBinaryString } from "./buffer";

const VERSION = "1";
const PREFIX = `#eml=${VERSION}:`;
export const MAX_PERMALINK_BYTES = 512 * 1024;

/**
 * Cap on the gunzip decompressed output to prevent a zip-bomb amplification.
 *
 * The compressed payload is capped at {@link MAX_PERMALINK_BYTES} (512 KiB),
 * but a 512 KiB gzip of zero-bytes inflates to roughly 512 MiB — a classic
 * zip bomb. Pre-fix the decoder allocated the full decompressed buffer before
 * any parser saw it, blowing up memory.
 *
 * The cap below is 8 MiB which is comfortably above any realistic `.eml`
 * payload (the parser itself rejects messages > ~5 MiB) while keeping the
 * worst-case memory footprint bounded.
 */
export const MAX_PERMALINK_DECOMPRESSED_BYTES = 8 * 1024 * 1024;

/**
 * "soft" warning threshold — when the projected compressed payload exceeds
 * this fraction of {@link MAX_PERMALINK_BYTES} we still encode but the caller
 * surfaces a UI warning so users know their link is close to the cap and may
 * break in clients that truncate long URLs (Slack/Outlook/SMS).
 */
export const PERMALINK_WARN_RATIO = 0.8;

/**
 * Predict the compressed payload size **before** building the URL. Returns
 * `bytes` (gzipped + base64url) and three derived booleans the UI uses to
 * choose between green / amber / red feedback. Pure size check — does not
 * touch {@link history} or the clipboard, so it's cheap to call from a
 * `useEffect` or render-time hint.
 */
export async function estimatePermalinkSize(source: string): Promise<{
  compressedBytes: number;
  ratioOfCap: number;
  shouldWarn: boolean;
  exceedsCap: boolean;
}> {
  const input = new TextEncoder().encode(source);
  const compressed = await gzip(input);
  const compressedBytes = compressed.byteLength;
  const ratioOfCap = compressedBytes / MAX_PERMALINK_BYTES;
  return {
    compressedBytes,
    ratioOfCap,
    shouldWarn: ratioOfCap >= PERMALINK_WARN_RATIO && ratioOfCap <= 1,
    exceedsCap: ratioOfCap > 1,
  };
}

/**
 * Tab-scoped filter snapshot encoded into a permalink. Both keys are
 * optional — encoders only include the active tab's slice and decoders
 * tolerate either being absent for forward/backward compatibility.
 */
export interface PermalinkFilters {
  headers?: { category?: string; query?: string };
  links?: { filter?: string; query?: string };
}

export interface PermalinkEncodeResult {
  hash: string;
  url: string;
  compressedBytes: number;
  ratio: number;
}

/**
 * M5 reproducibility — the running parser/app version, URL-safe.
 *
 * A permalink can carry `&v=<version>` so a reader knows which build captured
 * the message. We accept the common semver shape (digits, dots, plus an
 * optional `-prerelease` / `+build` tail using `[A-Za-z0-9.-]`) and nothing
 * else — anything outside this alphabet is dropped from the hash rather than
 * percent-encoded, mirroring the `tab=`/`entity=` gates. The full
 * version-pinned *replay* (decode-with-the-captured-parser) is a documented
 * follow-up; for now decode surfaces a provenance note + a mismatch warning.
 */
const PERMALINK_VERSION_RE = /^[A-Za-z0-9][A-Za-z0-9.+-]*$/;

export async function encodePermalink(
  source: string,
  options?: {
    tab?: string;
    filters?: PermalinkFilters;
    entity?: string;
    /** Parser/app version to stamp as `&v=<version>` (e.g. `"0.33.0"`). */
    version?: string;
  },
): Promise<PermalinkEncodeResult> {
  const input = new TextEncoder().encode(source);
  const compressed = await gzip(input);
  if (compressed.byteLength > MAX_PERMALINK_BYTES) {
    throw new Error(
      `Compressed source (${Math.round(
        compressed.byteLength / 1024,
      )} KiB) is too large to stuff in a URL hash (limit ${
        MAX_PERMALINK_BYTES / 1024
      } KiB).`,
    );
  }
  const tab = options?.tab?.trim();
  const tabSuffix =
    tab && /^[a-z0-9-]+$/i.test(tab) ? `&tab=${encodeURIComponent(tab)}` : "";
  const filtersSuffix = encodeFiltersSuffix(options?.filters);
  // Append `&entity=<id>` so the workbench can land on the sender's entity
  // after the message decodes. Same URL-safe gate as the bare-form decoder;
  // malformed ids are silently dropped from the hash rather than rejected.
  const entity = options?.entity?.trim();
  const entitySuffix = entity && ENTITY_ID_RE.test(entity) ? `&entity=${entity}` : "";
  // Append `&v=<version>` so a reader can see which build captured the
  // message. URL-safe gate; malformed versions are dropped (never
  // percent-encoded) so the hash stays clean.
  const version = options?.version?.trim();
  const versionSuffix =
    version && PERMALINK_VERSION_RE.test(version) ? `&v=${version}` : "";
  const hash =
    PREFIX +
    toBase64Url(compressed) +
    tabSuffix +
    filtersSuffix +
    entitySuffix +
    versionSuffix;
  const url =
    typeof window !== "undefined"
      ? window.location.origin + window.location.pathname + hash
      : hash;
  return {
    hash,
    url,
    compressedBytes: compressed.byteLength,
    ratio: input.byteLength ? compressed.byteLength / input.byteLength : 1,
  };
}

/**
 * Extract the optional `tab=<name>` segment from a permalink hash. Returns
 * the raw tab name (lowercase, hyphenated) or `null` when absent.
 */
export function extractPermalinkTab(hash: string): string | null {
  if (!hash || !hash.startsWith(PREFIX)) return null;
  const rest = stripPrefix(hash);
  const m = rest.match(/&tab=([a-z0-9-]+)(?:&|$)/i);
  return m && m[1] ? decodeURIComponent(m[1]) : null;
}

/**
 * Extract the optional `&v=<version>` provenance segment from a permalink
 * hash. Returns the captured parser/app version string or `null` when absent
 * or malformed. Used by {@link useInitialHash} to surface a "captured with
 * vX" note + warn when it differs from the running build.
 */
export function extractPermalinkVersion(hash: string): string | null {
  if (!hash || !hash.startsWith(PREFIX)) return null;
  const rest = stripPrefix(hash);
  const m = rest.match(/&v=([A-Za-z0-9][A-Za-z0-9.+-]*)(?:&|$)/);
  return m && m[1] ? m[1] : null;
}

/**
 * Opaque evidence id encoded in the permalink hash for `<EvidenceChip>`'s
 * deep-link round trip.
 *
 * The encoded `<id>` is a stable string the chip's `onJump` writes when
 * navigating; the Raw tab's existing deep-link effect picks it up and
 * resolves it to a header index / byte range / part path. For now the id is a
 * passthrough of `header-<index>`, `byte-<from>-<to>`, or `part-<dotted>` —
 * no global evidence registry yet (theme O7 lands later).
 *
 * Only `[A-Za-z0-9._-]` is accepted to keep the value URL-safe without a
 * percent-encoding round trip. Anything else returns `null`.
 */
const EVIDENCE_ID_RE = /^[A-Za-z0-9._-]+$/;

export function buildEvidenceHash(id: string): string | null {
  if (typeof id !== "string" || !EVIDENCE_ID_RE.test(id)) return null;
  return `#evidence=${id}`;
}

export function extractPermalinkEvidence(hash: string): string | null {
  if (!hash) return null;
  // Accept the bare `#evidence=…` shape (used when no permalink payload is
  // present — e.g. the user clicked a chip while inspecting a local file)
  // *and* the suffix shape `#eml=1:…&evidence=…` (used when the message
  // was loaded from a permalink).
  let body = hash;
  if (body.startsWith(PREFIX)) {
    body = stripPrefix(body);
    const m = body.match(/&evidence=([A-Za-z0-9._-]+)(?:&|$)/);
    return m && m[1] ? m[1] : null;
  }
  // Bare form. `#` followed by `evidence=<id>`, optionally with extra
  // `&key=value` segments after.
  const stripped = body.startsWith("#") ? body.slice(1) : body;
  const m = stripped.match(/(?:^|&)evidence=([A-Za-z0-9._-]+)(?:&|$)/);
  return m && m[1] ? m[1] : null;
}

/**
 * Workbench entity id encoded in the permalink hash for `#entity=<id>`
 * deep-links. Coexists with `#eml=`, `#header=`, and `&evidence=` — the
 * encoder appends, never replaces, so a shared `#eml=…&entity=auth:dkim` URL
 * lands on the DKIM entity after the parser decodes the message.
 *
 * Entity ids are colon-separated namespace pairs (e.g. `auth:dkim`,
 * `body:headers`, `envelope:from:user@example.com`). We allow `:` and
 * `@`/`.`/`+` for address-typed sub-entities, plus the same URL-safe alphabet
 * as evidence ids. Anything else returns `null` from the decoder so a
 * hand-mangled hash can't smuggle a percent-encoded payload through.
 *
 * Tightened from `/^[A-Za-z0-9._:@+-]+$/` to require a known top-level
 * namespace prefix (`auth`, `body`, `envelope`, `trust`, `routing`, `crypto`,
 * `attachment`, `link`) followed by `:` and a URL-safe id. This preserves
 * expressive power for every consumer in the codebase (verified by grep of
 * `entity:`-shaped strings across `src/`) while narrowing the parser surface:
 * a hand-crafted permalink can no longer smuggle in arbitrary
 * `colon@plus`-shaped tokens that would survive the regex but confuse a
 * downstream switch.
 */
const ENTITY_NAMESPACES = [
  "auth",
  "body",
  "defects",
  "envelope",
  "indicators",
  "routing",
  "source",
  "trust",
  // Reserved for future use by P5-era surfaces. Listed here so a
  // permalink generated by a newer build still decodes on an older
  // client (forwards-compatible enum).
  "attachment",
  "crypto",
  "link",
] as const;

const ENTITY_ID_RE = new RegExp(
  `^(?:${ENTITY_NAMESPACES.join("|")}):[A-Za-z0-9._@+-]+(?::[A-Za-z0-9._@+-]+)*$`,
);

/**
 * Build a bare `#entity=<id>` hash for the case when no permalink
 * payload is present (e.g. the user opened a local file and wants to
 * pre-select an entity via the URL). Returns `null` when the id is
 * malformed — the caller leaves the URL alone.
 */
export function buildEntityHash(id: string): string | null {
  if (typeof id !== "string" || !ENTITY_ID_RE.test(id)) return null;
  return `#entity=${id}`;
}

/**
 * Append (or replace) the `&entity=<id>` segment on the current
 * `window.location.hash` via `history.replaceState`. Coexists with
 * the existing `#eml=…`, `#header=…`, and `&evidence=…` segments —
 * other segments are preserved verbatim. Pass `null` to remove the
 * entity segment.
 *
 * No-op when `window` is undefined (SSR / jsdom-less unit tests).
 */
export function writeWorkbenchEntityHash(id: string | null): void {
  if (typeof window === "undefined") return;
  if (id !== null && !ENTITY_ID_RE.test(id)) return;
  try {
    const url = new URL(window.location.href);
    const current = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
    const next = upsertHashSegment(current, "entity", id);
    if (next === current) return;
    url.hash = next ? `#${next}` : "";
    window.history.replaceState(null, "", url.toString());
  } catch {
    /* invalid URL — leave alone */
  }
}

/**
 * Internal helper: upsert a `key=value` segment inside a hash body
 * (without the leading `#`). The body may start with `eml=1:…`
 * (permalink payload) or be a flat `key=value&key=value` query. We
 * preserve segment order: existing `key=…` is replaced in place;
 * missing keys are appended after the last existing segment.
 *
 * Passing `value = null` removes the segment. Passing an empty string
 * is treated as removal too (you can't encode an empty entity id).
 */
function upsertHashSegment(body: string, key: string, value: string | null): string {
  if (!body) return value ? `${key}=${value}` : "";
  // Split on `&` for the suffix-style permalink (`eml=…&entity=…`) and
  // the bare form (`entity=…&header=…`) uniformly. The permalink prefix
  // is itself an `eml=…` segment so we don't have to special-case it.
  const segments = body.split("&");
  const idx = segments.findIndex((s) => s.startsWith(`${key}=`));
  if (idx === -1) {
    if (!value) return body;
    return [...segments, `${key}=${value}`].join("&");
  }
  if (!value) {
    segments.splice(idx, 1);
    return segments.join("&");
  }
  segments[idx] = `${key}=${value}`;
  return segments.join("&");
}

/**
 * Extract the `#entity=<id>` (bare form) or `&entity=<id>` (suffix
 * form, after a `#eml=…` payload) segment from the hash. Returns
 * `null` when absent, malformed, or fails the
 * URL-safe-alphabet check.
 */
export function extractPermalinkEntity(hash: string): string | null {
  if (!hash) return null;
  // Suffix form: hash starts with the `#eml=1:` payload.
  if (hash.startsWith(PREFIX)) {
    const body = stripPrefix(hash);
    const m = body.match(/&entity=([A-Za-z0-9._:@+-]+)(?:&|$)/);
    return m && m[1] ? m[1] : null;
  }
  // Bare form. Strip the leading `#` so the regex anchors against
  // either the start of the body or an `&` separator.
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;
  const m = stripped.match(/(?:^|&)entity=([A-Za-z0-9._:@+-]+)(?:&|$)/);
  return m && m[1] ? m[1] : null;
}

/**
 * Extract the optional `&filters=<base64url-json>` segment from a permalink
 * hash. Returns a normalised {@link PermalinkFilters} object (always
 * shallow-cloned, with unknown fields stripped) or `null` when absent or
 * unparseable.
 */
export function extractPermalinkFilters(hash: string): PermalinkFilters | null {
  if (!hash || !hash.startsWith(PREFIX)) return null;
  const rest = stripPrefix(hash);
  const m = rest.match(/&filters=([A-Za-z0-9_-]+)(?:&|$)/);
  if (!m || !m[1]) return null;
  try {
    const json = new TextDecoder("utf-8").decode(fromBase64Url(m[1]));
    const raw = JSON.parse(json) as unknown;
    return normaliseFilters(raw);
  } catch {
    return null;
  }
}

export async function decodePermalink(hash: string): Promise<string | null> {
  const result = await decodePermalinkDetailed(hash);
  return result.ok ? result.source : null;
}

/**
 * Structured permalink decode surface. Pre-fix, `decodePermalink` returned
 * `null` for every failure mode (no payload, hand-edited hash, truncated
 * base64, decompression error, version mismatch), and the caller silently
 * landed the user on an empty editor — broken shared links produced no toast
 * / banner / banner. The structured shape lets {@link useInitialHash}
 * discriminate between "no payload to decode" (the bare app load with
 * `#headers` or no hash at all — silent is correct) and "the user clicked a
 * link that should have loaded a message but couldn't" (surface a toast +
 * banner with the underlying reason for "Show details").
 *
 * - `kind: "no-permalink"` — the hash doesn't carry an `#eml=` payload at
 * all. Used by the bare `#tab` route, `#workspace/<id>`, etc. Callers should
 * treat this as "nothing to load" and stay silent. - `kind:
 * "version-mismatch"` — the prefix matched `#eml=` but the version segment
 * (`2:`, `3:`, …) doesn't match {@link VERSION}. Future-format permalink; the
 * user needs a newer build. - `kind: "truncated"` — base64 decode failed (the
 * payload was truncated by a chat client, URL bar wrap, or hand-edit). -
 * `kind: "corrupted"` — gunzip / utf-8 decode failed (the payload has the
 * right base64 alphabet but isn't a valid gzip stream). - `kind: "unknown"` —
 * any other thrown error inside the decode pipeline. `detail` carries the raw
 * message.
 */
export type PermalinkDecodeResult =
  | {
      ok: true;
      source: string;
      /**
       * The `&v=<version>` provenance stamp, when the permalink carried one.
       * `null` for older / version-less links. The caller surfaces a
       * "captured with vX" note and warns when it differs from the running
       * build (full version-pinned replay is a documented follow-up).
       */
      capturedVersion: string | null;
    }
  | {
      ok: false;
      kind: "no-permalink" | "version-mismatch" | "truncated" | "corrupted" | "unknown";
      detail: string;
    };

export async function decodePermalinkDetailed(
  hash: string,
): Promise<PermalinkDecodeResult> {
  if (!hash) {
    return { ok: false, kind: "no-permalink", detail: "empty hash" };
  }
  // Recognise the `#eml=` family without binding to the version segment
  // yet — that's the version-mismatch surface below.
  if (!hash.startsWith("#eml=")) {
    return { ok: false, kind: "no-permalink", detail: "no #eml= prefix" };
  }
  if (!hash.startsWith(PREFIX)) {
    // Hash starts `#eml=` but the version segment isn't `1:`. The user
    // clicked a permalink built by a future (or fork) version of the
    // app — surface the mismatch instead of falling through to
    // truncated.
    const versionMatch = hash.match(/^#eml=([^:]+):/);
    const claimed = versionMatch?.[1] ?? "(missing)";
    return {
      ok: false,
      kind: "version-mismatch",
      detail: `expected version "${VERSION}", got "${claimed}"`,
    };
  }
  const rest = stripPrefix(hash);
  // Strip the optional &tab=… and &filters=… suffixes before base64-decoding.
  // Old permalinks (no suffixes at all) and future-extra suffixes both
  // survive: anything after the first `&` is a query-style segment.
  const ampIdx = rest.indexOf("&");
  const encoded = ampIdx === -1 ? rest : rest.slice(0, ampIdx);
  if (!encoded) {
    return { ok: false, kind: "truncated", detail: "empty payload" };
  }
  let bytes: Uint8Array;
  try {
    bytes = fromBase64Url(encoded);
  } catch (err) {
    return {
      ok: false,
      kind: "truncated",
      detail: err instanceof Error ? err.message : String(err),
    };
  }
  try {
    const decompressed = await gunzip(bytes);
    return {
      ok: true,
      source: new TextDecoder("utf-8").decode(decompressed),
      capturedVersion: extractPermalinkVersion(hash),
    };
  } catch (err) {
    // gunzip / utf-8 decode threw. The payload has the right base64
    // alphabet (we got past `fromBase64Url`) but isn't valid gzip.
    // Mid-URL truncation lands here too — Outlook is known to wrap on
    // 76 chars and lose the final block.
    return {
      ok: false,
      kind: "corrupted",
      detail: err instanceof Error ? err.message : String(err),
    };
  }
}

function stripPrefix(hash: string): string {
  return hash.slice(PREFIX.length);
}

/** Encode the optional filters payload as `&filters=<base64url-json>`. */
function encodeFiltersSuffix(filters: PermalinkFilters | undefined): string {
  if (!filters) return "";
  const cleaned = normaliseFilters(filters);
  if (!cleaned) return "";
  const json = JSON.stringify(cleaned);
  const bytes = new TextEncoder().encode(json);
  return `&filters=${toBase64Url(bytes)}`;
}

/**
 * Strip unknown fields and coerce values to strings so an attacker (or a
 * stale encoder) can't smuggle structured payloads through the hash.
 * Returns `null` when nothing useful survives.
 */
function normaliseFilters(raw: unknown): PermalinkFilters | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const out: PermalinkFilters = {};

  const h = r.headers;
  if (typeof h === "object" && h !== null) {
    const ho = h as Record<string, unknown>;
    const category = typeof ho.category === "string" ? ho.category : undefined;
    const query = typeof ho.query === "string" ? ho.query : undefined;
    if (category !== undefined || query !== undefined) {
      out.headers = {};
      if (category !== undefined) out.headers.category = category;
      if (query !== undefined) out.headers.query = query;
    }
  }

  const l = r.links;
  if (typeof l === "object" && l !== null) {
    const lo = l as Record<string, unknown>;
    const filter = typeof lo.filter === "string" ? lo.filter : undefined;
    const query = typeof lo.query === "string" ? lo.query : undefined;
    if (filter !== undefined || query !== undefined) {
      out.links = {};
      if (filter !== undefined) out.links.filter = filter;
      if (query !== undefined) out.links.query = query;
    }
  }

  return out.headers || out.links ? out : null;
}

export function clearPermalink(): void {
  if (typeof window === "undefined") return;
  if (window.location.hash.startsWith(PREFIX)) {
    history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

// ---------- gzip / gunzip via Streams API ----------

async function gzip(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([asBlobPart(data)])
    .stream()
    .pipeThrough(new CompressionStream("gzip"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Bounded gunzip.
 *
 * Reads the `DecompressionStream("gzip")` output chunk-by-chunk, accumulating
 * bytes until either: - the stream closes naturally (success — return the
 * assembled buffer), or - the decompressed total crosses {@link
 * MAX_PERMALINK_DECOMPRESSED_BYTES} (failure — cancel the stream and throw,
 * which the caller maps to a `corrupted` decode result).
 *
 * The reader cancels the underlying stream on overflow so the remaining
 * (uncomputed) chunks are dropped rather than allocated. Counting bytes per
 * chunk keeps the peak memory footprint to the accumulated decompressed
 * buffer plus one chunk.
 */
async function gunzip(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([asBlobPart(data)])
    .stream()
    .pipeThrough(new DecompressionStream("gzip"));
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      if (value) {
        total += value.byteLength;
        if (total > MAX_PERMALINK_DECOMPRESSED_BYTES) {
          // Abort the underlying stream so the decompressor stops
          // producing further chunks. The catch caller turns this into
          // a `corrupted` decode result.
          await reader.cancel();
          throw new Error(
            `decompressed payload exceeded cap (${MAX_PERMALINK_DECOMPRESSED_BYTES} bytes)`,
          );
        }
        chunks.push(value);
      }
    }
  } finally {
    reader.releaseLock();
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}

// ---------- base64url helpers ----------

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, Math.min(i + chunk, bytes.length));
    binary += bytesToBinaryString(slice);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(s: string): Uint8Array {
  const padded =
    s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
